import type { AsyncDataRequestStatus } from '#app'

export type ProductFetchOutcome = 'ok' | 'throw-404' | 'ssr-degraded' | 'retrying' | 'stale-retrying' | 'inline-error' | 'stale-banner'

export interface ProductFetchState {
  statusCode: number | null
  hasProduct: boolean
  isServer: boolean
  autoRetryDone: boolean
}

export function resolveProductFetchOutcome({ statusCode, hasProduct, isServer, autoRetryDone }: ProductFetchState): ProductFetchOutcome {
  if (!statusCode) return 'ok'
  if (statusCode === 404) return 'throw-404'
  if (hasProduct) return autoRetryDone ? 'stale-banner' : 'stale-retrying'
  if (isServer) return 'ssr-degraded'
  return autoRetryDone ? 'inline-error' : 'retrying'
}

// Also blocks re-entry while the auto-retry's own request is in flight, not just the first load.
export function isProductFetchUnsettled(status: AsyncDataRequestStatus, hasProduct: boolean): boolean {
  return !hasProduct && (status === 'idle' || status === 'pending')
}

export interface ProductFetchErrorData {
  transportFailure?: boolean
  attempts?: number
}

export function normalizeProductFetchStatus(error: unknown): number {
  return Number((error as { statusCode?: unknown })?.statusCode) || 500
}

// A fetch that never got an HTTP status never reached the backend — the shopper's connection dropped.
export function isProductTransportFailure(error: unknown): boolean {
  return (error as { data?: ProductFetchErrorData })?.data?.transportFailure === true
}

export function getProductFetchAttempts(error: unknown): number {
  const attempts = (error as { data?: ProductFetchErrorData })?.data?.attempts
  return Number.isInteger(attempts) && (attempts as number) >= 1 ? (attempts as number) : 1
}

export function isRetriableProductFetchStatus(statusCode: number | null, transportFailure: boolean): boolean {
  if (transportFailure) return true
  return statusCode == null || statusCode >= 500
}

export function isGoneStatus(statusCode: unknown): boolean {
  return statusCode === 404 || statusCode === 410
}
